import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  getTimeFromDate,
  showNotification,
  TimeType,
} from 'platform/components';
import {Grid, VStack, Show} from 'platform/foundation';
import {z} from 'zod';

import {useMemo} from 'react';

import {isNil} from 'ramda';

import {useCreateUserAbsenceMutation, useUpdateUserAbsenceMutation} from '@dms/api/customer';
import {UserAbsenceResponseBody} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {handleApiError, refreshDmsDatagrid, useGetUserOptions} from '@dms/shared';

import {getApiDateString, parseDate, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {USER_ABSENCE_GRID_CODE, USER_ABSENCE_OVERVIEW_GRID_CODE} from '../constants/constants';
import {getUserAbsenceRequestBody} from '../utils/getUserAbsenceRequestBody';
import {userAbsenceAdminFormSchema, userAbsenceFormSchema} from '../utils/userAbsenceFormSchema';

const DEFAULT_DATE_FROM_TIME: TimeType = '00:00';
const DEFAULT_DATE_TO_TIME: TimeType = '23:59';

type UserAbsenceAdminFormType = z.infer<typeof userAbsenceAdminFormSchema>;

type UserAbsenceFormDialogBaseProps = RequiredTestIdProps & {
  absence?: UserAbsenceResponseBody;
  onSuccess?: () => void;
};

type UserAbsenceFormDialogProps = UserAbsenceFormDialogBaseProps &
  ({allowUserSelection: true; userId?: never} | {allowUserSelection?: false; userId: string});

export function UserAbsenceFormDialog(props: UserAbsenceFormDialogProps) {
  const isAdminForm = props.allowUserSelection === true;
  const {userOptions, isLoading: isUserOptionsLoading} = useGetUserOptions({
    withAvailabilitySummary: false,
  });
  const [createUserAbsence, {isLoading: isCreateLoading}] = useCreateUserAbsenceMutation();
  const [updateUserAbsence, {isLoading: isUpdateLoading}] = useUpdateUserAbsenceMutation();
  const formSchema = useMemo(() => {
    const baseSchema = isAdminForm ? userAbsenceAdminFormSchema : userAbsenceFormSchema;

    return baseSchema.superRefine((values, context) => {
      const userId = isAdminForm ? (values as UserAbsenceAdminFormType).userId : props.userId;

      if (values.absenceContactUserId === userId) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['absenceContactUserId'],
          message: i18n.t('entity.userAbsence.errors.contactUserSameAsUser'),
        });
      }
    });
  }, [isAdminForm, props.userId]);

  const defaultValues = {
    dateFrom: props.absence?.dateFrom ? getApiDateString(parseDate(props.absence.dateFrom)) : '',
    dateFromTime: props.absence?.dateFrom
      ? getTimeFromDate(parseDate(props.absence.dateFrom))
      : DEFAULT_DATE_FROM_TIME,
    dateTo: props.absence?.dateTo ? getApiDateString(parseDate(props.absence.dateTo)) : '',
    dateToTime: props.absence?.dateTo
      ? getTimeFromDate(parseDate(props.absence.dateTo))
      : DEFAULT_DATE_TO_TIME,
    absenceContactUserId: props.absence?.absenceContactUser?.id ?? null,
    description: props.absence?.description ?? null,
    forwardEmailsToDelegate: props.absence?.forwardEmailsToDelegate ?? false,
    ...(isAdminForm ? {userId: '' as const} : {userId: '' as const}),
  };

  const handleSuccess = () => {
    refreshDmsDatagrid(USER_ABSENCE_GRID_CODE);
    refreshDmsDatagrid(USER_ABSENCE_OVERVIEW_GRID_CODE);
    props.onSuccess?.();
    showNotification.success(
      i18n.t(`general.notifications.successfully${props.absence ? 'Updated' : 'Created'}`)
    );
    closeCurrentDialog();
  };

  const handleSubmit: FormSubmitHandler<UserAbsenceAdminFormType> = (values) => {
    const userId = isAdminForm ? values.userId : props.userId;
    const payload = getUserAbsenceRequestBody(values);

    const request = props.absence
      ? updateUserAbsence({
          userId,
          userAbsenceId: props.absence.id,
          userAbsenceRequestBody: payload,
        })
      : createUserAbsence({
          userId,
          userAbsenceRequestBody: payload,
        });

    return request.unwrap().then(handleSuccess).catch(handleApiError);
  };

  return (
    <DataStatus isLoading={isUserOptionsLoading}>
      <Form<UserAbsenceAdminFormType>
        defaultValues={defaultValues}
        experimentalZodSchema={formSchema}
        onSubmit={handleSubmit}
      >
        {(control, formApi) => {
          const selectedUserId = isAdminForm ? formApi.watch('userId') : props.userId;
          const absenceContactUserOptions = userOptions.filter(
            (option) => option.value !== selectedUserId
          );
          const absenceContactUserId = formApi.watch('absenceContactUserId');

          const handleAbsenceContactUserChange = (value: string | number | null) => {
            if (isNil(value)) {
              formApi.setValue('forwardEmailsToDelegate', false);
            }
          };

          return (
            <VStack spacing={4}>
              <Show when={isAdminForm}>
                <FormField
                  control={control}
                  name="userId"
                  type="choice"
                  label={i18n.t('entity.user.labels.users')}
                  options={userOptions}
                  menuInPortal
                  isRequired
                />
              </Show>
              <Grid columns={2} verticalSpacing={4}>
                <FormField
                  control={control}
                  name="dateFrom"
                  type="apiDate"
                  label={i18n.t('general.labels.from')}
                  isRequired
                />
                <FormField
                  control={control}
                  name="dateFromTime"
                  type="time"
                  label={i18n.t('general.labels.time')}
                  isRequired
                />
                <FormField
                  control={control}
                  name="dateTo"
                  type="apiDate"
                  minDate={parseDate(formApi.watch('dateFrom')) ?? undefined}
                  label={i18n.t('general.labels.to')}
                  helperText={i18n.t('entity.userAbsence.helpers.dateTo')}
                />
                <FormField
                  control={control}
                  name="dateToTime"
                  type="time"
                  minTime={
                    formApi.watch('dateTo') === formApi.watch('dateFrom')
                      ? (formApi.watch('dateFromTime') as TimeType)
                      : undefined
                  }
                  label={i18n.t('general.labels.time')}
                />
              </Grid>
              <FormField
                control={control}
                name="absenceContactUserId"
                type="choice"
                label={i18n.t('entity.userAbsence.labels.absenceContactUser')}
                helperText={i18n.t('entity.userAbsence.helpers.absenceContactUser')}
                options={absenceContactUserOptions}
                onChange={handleAbsenceContactUserChange}
                menuInPortal
              />
              <FormField
                control={control}
                name="forwardEmailsToDelegate"
                type="checkbox"
                isDisabled={isNil(absenceContactUserId)}
                label={i18n.t('entity.userAbsence.labels.forwardEmailsToContactUser')}
                helperText={i18n.t('entity.userAbsence.helpers.forwardEmailsToContactUser')}
                data-testid={suffixTestId('forwardEmailsToDelegate', props)}
              />
              <FormField
                control={control}
                name="description"
                type="text"
                label={i18n.t('general.labels.description')}
              />
              <ButtonGroup align="right">
                <Button
                  variant="secondary"
                  title={i18n.t('general.actions.cancel')}
                  onClick={closeCurrentDialog}
                  data-testid={suffixTestId('cancel-button', props)}
                />
                <FormButton
                  control={control}
                  type="submit"
                  title={i18n.t('general.actions.save')}
                  isLoading={isCreateLoading || isUpdateLoading}
                  data-testid={suffixTestId('save-button', props)}
                />
              </ButtonGroup>
            </VStack>
          );
        }}
      </Form>
    </DataStatus>
  );
}
